import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    CheckCircle2,
    ExternalLink,
    KeyRound,
    Loader2,
    Package,
    RefreshCw,
    Search,
    Settings,
    ShieldCheck,
    Sparkles,
    TrendingUp,
    FileText,
} from "lucide-react";
import { toast } from "sonner";
import { bilusParca } from "../../lib/api";

const TABS = [
    { id: "komuta", label: "Komuta", icon: Sparkles },
    { id: "ara", label: "Parça Ara", icon: Search },
    { id: "dosya", label: "Dosya / Evrak", icon: Package },
    { id: "ayar", label: "Üye Ayarları", icon: Settings },
];

function tl(n, fmt) {
    if (fmt) return fmt;
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return `₺${v.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;
}

function DurumBadge({ durum, label }) {
    const cls =
        durum === "yuksek"
            ? "border-rose-500/40 text-rose-300 bg-rose-500/10"
            : durum === "uyumlu"
              ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
              : durum === "bilgi"
                ? "border-cyan-500/40 text-cyan-300 bg-cyan-500/10"
                : "border-slate-500/40 text-slate-400 bg-slate-500/10";
    return (
        <span className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
            {label || durum || "—"}
        </span>
    );
}

function KpiCard({ label, value, hint, accent = "gold" }) {
    const ring =
        accent === "emerald"
            ? "border-emerald-500/30 bg-emerald-500/5"
            : accent === "amber"
              ? "border-amber-500/30 bg-amber-500/5"
              : accent === "cyan"
                ? "border-cyan-500/30 bg-cyan-500/5"
                : "border-[#D4AF37]/25 bg-[#121A2F]/80";
    return (
        <div className={`rounded-xl border p-4 ${ring}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display text-2xl text-white mt-1">{value ?? "—"}</div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
        </div>
    );
}

function SonucKart({ item, onFiyat }) {
    const [fiyatBusy, setFiyatBusy] = useState(false);
    const yukleFiyat = async () => {
        setFiyatBusy(true);
        try {
            await onFiyat(item.oem_id);
        } finally {
            setFiyatBusy(false);
        }
    };
    return (
        <article className="rounded-xl border border-orange-500/25 bg-gradient-to-br from-orange-500/8 to-[#0B0F19]/90 p-4">
            <div className="flex justify-between gap-2 mb-2">
                <h4 className="text-sm font-medium text-white">{item.baslik || "—"}</h4>
                {item.oem_kod ? (
                    <span className="text-[10px] font-mono text-orange-300 bg-orange-500/10 px-2 py-0.5 rounded shrink-0">
                        {item.oem_kod}
                    </span>
                ) : null}
            </div>
            {(item.muadil_markalar || []).length > 0 ? (
                <div className="text-[10px] text-slate-500 mb-2">
                    Muadil: {(item.muadil_markalar || []).join(", ")}
                </div>
            ) : null}
            {item.guncel_fiyat ? (
                <div className="text-xs text-emerald-300 mb-2">
                    Güncel: {item.guncel_fiyat.kdvli ?? item.guncel_fiyat.kdvsiz}{" "}
                    {item.guncel_fiyat.birim}
                </div>
            ) : null}
            <div className="flex gap-2 mt-2">
                <button
                    type="button"
                    onClick={yukleFiyat}
                    disabled={fiyatBusy}
                    className="text-[10px] px-2 py-1 rounded bg-orange-600/70 text-white disabled:opacity-50"
                >
                    {fiyatBusy ? <Loader2 className="h-3 w-3 animate-spin inline" /> : "Fiyat geçmişi"}
                </button>
                <a
                    href={`https://www.bilus.com.tr/oem/${item.oem_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] px-2 py-1 rounded border border-white/10 text-slate-400 inline-flex items-center gap-1"
                >
                    <ExternalLink className="h-3 w-3" /> Bilus
                </a>
            </div>
        </article>
    );
}

export default function BilusParcaMerkezPage() {
    const [tab, setTab] = useState("komuta");
    const [ozet, setOzet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sorgu, setSorgu] = useState("");
    const [marka, setMarka] = useState("oem");
    const [aramaSonuc, setAramaSonuc] = useState(null);
    const [dosyaNo, setDosyaNo] = useState("");
    const [evrakList, setEvrakList] = useState(null);
    const [seciliEvrak, setSeciliEvrak] = useState([]);
    const [karsilastirma, setKarsilastirma] = useState(null);
    const [ayar, setAyar] = useState({ eposta: "", sifre: "", aktif: true });
    const [ayarSifre, setAyarSifre] = useState("");

    const loadOzet = useCallback(async () => {
        try {
            const [o, a] = await Promise.all([bilusParca.ozet(), bilusParca.ayarGet()]);
            setOzet(o);
            setAyar((prev) => ({ ...prev, eposta: a.eposta || "", aktif: a.aktif !== false }));
        } catch {
            toast.error("Bilus modülü yüklenemedi");
        }
    }, []);

    useEffect(() => {
        loadOzet();
    }, [loadOzet]);

    const runAra = async (fiyatCek = false) => {
        if (!sorgu.trim()) {
            toast.error("OEM kod veya parça adı girin");
            return;
        }
        setLoading(true);
        try {
            const r = await bilusParca.ara({ sorgu: sorgu.trim(), marka, fiyat_cek: fiyatCek });
            if (!r.success) throw new Error(r.error);
            setAramaSonuc(r);
            toast.success(`${r.adet || 0} sonuç — kontör: ${r.kalan_kontor ?? "?"}`);
            loadOzet();
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Arama başarısız");
        } finally {
            setLoading(false);
        }
    };

    const runKarsilastir = async (fiyatCek = false) => {
        if (!dosyaNo.trim()) {
            toast.error("Sigorta dosya numarası girin");
            return;
        }
        setLoading(true);
        try {
            const r = await bilusParca.karsilastir({
                dosya_no: dosyaNo.trim(),
                fiyat_cek: fiyatCek,
            });
            if (!r.success) throw new Error(r.error);
            setKarsilastirma(r);
            toast.success(r.ozet?.mesaj || "Karşılaştırma tamam");
            loadOzet();
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Karşılaştırma başarısız");
        } finally {
            setLoading(false);
        }
    };

    const loadEvrak = async () => {
        if (!dosyaNo.trim()) {
            toast.error("Sigorta dosya numarası girin");
            return;
        }
        setLoading(true);
        try {
            const r = await bilusParca.dosyaEvrak(dosyaNo.trim());
            if (!r.success) throw new Error(r.error);
            setEvrakList(r);
            const uygun = (r.evraklar || []).filter((e) => e.bilus_tarama_uygun).map((e) => e.id);
            setSeciliEvrak(uygun);
            toast.success(`${r.uygun_evrak_sayisi || 0} uygun evrak bulundu`);
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Evrak listesi alınamadı");
        } finally {
            setLoading(false);
        }
    };

    const runEvrakTara = async (fiyatCek = false) => {
        if (!dosyaNo.trim()) {
            toast.error("Sigorta dosya numarası girin");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                dosya_no: dosyaNo.trim(),
                fiyat_cek: fiyatCek,
                max_parca: 20,
            };
            if (seciliEvrak.length) payload.evrak_ids = seciliEvrak;
            const r = await bilusParca.evrakTara(payload);
            if (!r.success) throw new Error(r.error);
            setKarsilastirma(r);
            toast.success(r.ozet?.mesaj || "Evrak taraması tamam");
            loadOzet();
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Evrak taraması başarısız");
        } finally {
            setLoading(false);
        }
    };

    const satirFiyat = async (oemId, satirIdx) => {
        if (!oemId) return;
        try {
            const f = await bilusParca.fiyat(oemId);
            if (!f.success) throw new Error(f.error);
            setKarsilastirma((prev) => {
                if (!prev?.parcalar_sonuc) return prev;
                const parcalar_sonuc = prev.parcalar_sonuc.map((p, i) =>
                    i === satirIdx
                        ? {
                              ...p,
                              guncel_fiyat: f.guncel,
                              referans_fiyat: f.guncel?.kdvsiz ?? f.guncel?.kdvli,
                              referans_fmt: f.guncel?.kdvli ?? f.guncel?.kdvsiz,
                              fiyat_cekildi: true,
                              kontor_harcanan: 1,
                          }
                        : p
                );
                return {
                    ...prev,
                    parcalar_sonuc,
                    ozet: { ...prev.ozet, kalan_kontor: f.kalan_kontor ?? prev.ozet?.kalan_kontor },
                };
            });
            toast.success("Fiyat yüklendi (1 kontör)");
            loadOzet();
        } catch (e) {
            toast.error(e?.response?.data?.error || "Fiyat alınamadı");
        }
    };

    const kaydetAyar = async () => {
        setLoading(true);
        try {
            const payload = { eposta: ayar.eposta, aktif: ayar.aktif };
            if (ayarSifre.trim()) payload.sifre = ayarSifre;
            await bilusParca.ayarKaydet(payload);
            toast.success("Bilus ayarları kaydedildi");
            setAyarSifre("");
            loadOzet();
        } catch {
            toast.error("Ayar kaydedilemedi");
        } finally {
            setLoading(false);
        }
    };

    const testGiris = async () => {
        setLoading(true);
        try {
            const r = await bilusParca.girisTest();
            if (r.ok) toast.success(`Giriş OK — kontör: ${r.kalan_kontor ?? "?"}`);
            else toast.error(r.error || "Giriş başarısız");
            loadOzet();
        } catch (e) {
            toast.error(e?.response?.data?.error || "Giriş testi başarısız");
        } finally {
            setLoading(false);
        }
    };

    const tekFiyat = async (oemId) => {
        try {
            const f = await bilusParca.fiyat(oemId);
            if (!f.success) throw new Error(f.error);
            setAramaSonuc((prev) => {
                if (!prev?.items) return prev;
                return {
                    ...prev,
                    items: prev.items.map((it) =>
                        String(it.oem_id) === String(oemId)
                            ? { ...it, guncel_fiyat: f.guncel, fiyat_gecmisi: f.satirlar }
                            : it
                    ),
                    kalan_kontor: f.kalan_kontor ?? prev.kalan_kontor,
                };
            });
            toast.success("Fiyat geçmişi yüklendi (1 kontör)");
            loadOzet();
        } catch (e) {
            toast.error(e?.response?.data?.error || "Fiyat alınamadı");
        }
    };

    return (
        <div className="p-4 lg:p-8 max-w-[1600px] mx-auto animate-fade-in">
            <header className="rounded-2xl border border-orange-500/25 bg-gradient-to-br from-[#121A2F] via-[#0f1424] to-[#1a1208] p-5 md:p-7 mb-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/35 bg-orange-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-orange-200 mb-3">
                    <Package className="h-3.5 w-3.5" />
                    Bilus Entegrasyonu
                </div>
                <h1 className="font-display text-2xl md:text-3xl text-white">Bilus Parça Merkezi</h1>
                <p className="text-sm text-slate-400 mt-2 max-w-2xl">
                    bilus.com.tr üye oturumu ile OEM kod arama, muadil marka eşdeğerleri ve yetkili servis
                    fiyat geçmişi — Partsouq modülünün Türkiye alternatifi.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                    <a
                        href="https://www.bilus.com.tr/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-orange-300 hover:text-orange-200"
                    >
                        <ExternalLink className="h-3.5 w-3.5" /> bilus.com.tr
                    </a>
                    {ozet?.kalan_kontor != null ? (
                        <span className="text-xs text-slate-500">
                            Kalan kontör: <strong className="text-white">{ozet.kalan_kontor}</strong>
                        </span>
                    ) : null}
                </div>
            </header>

            <div className="flex flex-wrap gap-1.5 p-1 rounded-xl border border-white/10 bg-[#0B0F19]/80 mb-5">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${
                            tab === t.id
                                ? "bg-orange-600/40 text-white border border-orange-500/40"
                                : "text-slate-400 hover:text-white hover:bg-white/5"
                        }`}
                    >
                        <t.icon className="h-4 w-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "komuta" && (
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
                        <KpiCard
                            label="Kalan kontör"
                            value={ozet?.kalan_kontor ?? "—"}
                            hint="Fiyat gösterimi başına 1 kontör"
                            accent="amber"
                        />
                        <KpiCard
                            label="Markalı dosya"
                            value={ozet?.markali_dosya}
                            hint="Karşılaştırma için hazır"
                            accent="cyan"
                        />
                        <KpiCard
                            label="Oturum"
                            value={ozet?.son_giris ? "Aktif" : "—"}
                            hint={ozet?.eposta || "E-posta tanımlayın"}
                            accent="emerald"
                        />
                        <KpiCard
                            label="Durum"
                            value={ozet?.aktif ? "Açık" : "Kapalı"}
                            hint={ozet?.son_hata || "Üye girişi hazır"}
                            accent={ozet?.son_hata ? "amber" : "gold"}
                        />
                    </div>

                    {ozet?.son_hata ? (
                        <div className="flex items-center gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            {ozet.son_hata}
                        </div>
                    ) : null}

                    <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4 text-xs text-cyan-100/90">
                        <strong>Nasıl çalışır:</strong> Sistem arka planda Bilus&apos;a üye girişi yapar.
                        OEM kod veya parça adı ile arama yapılır; fiyat geçmişi isteğe bağlı çekilir (kontör
                        harcar). Dosya karşılaştırma modülü değer kaybı parçalarını otomatik tarar.
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => setTab("ayar")}
                            className="px-4 py-2 rounded-lg bg-orange-600/80 text-white text-sm font-medium"
                        >
                            <KeyRound className="h-4 w-4 inline mr-1" />
                            Üye bilgilerini ayarla
                        </button>
                        <button
                            type="button"
                            onClick={testGiris}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300"
                        >
                            Giriş testi
                        </button>
                        <Link
                            to="/yeni/partsouq-oem-karsilastirma"
                            className="px-4 py-2 rounded-lg border border-violet-500/30 text-sm text-violet-300"
                        >
                            Partsouq OEM modülü
                        </Link>
                    </div>
                </div>
            )}

            {tab === "ara" && (
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-2 items-end rounded-xl border border-white/10 bg-[#121A2F]/50 p-4">
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-[10px] text-slate-500 uppercase">OEM kod / parça</label>
                            <input
                                value={sorgu}
                                onChange={(e) => setSorgu(e.target.value)}
                                placeholder="63117182518 veya kaput"
                                className="w-full mt-1 rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                                onKeyDown={(e) => e.key === "Enter" && runAra(false)}
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-slate-500 uppercase">Marka</label>
                            <select
                                value={marka}
                                onChange={(e) => setMarka(e.target.value)}
                                className="mt-1 block rounded-lg bg-[#0B0F19] border border-white/10 px-2 py-2 text-sm text-white min-w-[140px]"
                            >
                                {(ozet?.markalar || [{ value: "oem", label: "Tüm Araçlar" }]).map((m) => (
                                    <option key={m.value} value={m.value}>
                                        {m.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={() => runAra(false)}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium disabled:opacity-50"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ara"}
                        </button>
                        <button
                            type="button"
                            onClick={() => runAra(true)}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg border border-orange-500/40 text-orange-200 text-sm disabled:opacity-50"
                            title="Kontör harcar"
                        >
                            Ara + fiyat
                        </button>
                    </div>

                    {aramaSonuc?.items?.length > 0 ? (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                            {aramaSonuc.items.map((it) => (
                                <SonucKart key={it.oem_id} item={it} onFiyat={tekFiyat} />
                            ))}
                        </div>
                    ) : aramaSonuc ? (
                        <div className="text-center text-slate-500 py-12">Sonuç bulunamadı</div>
                    ) : null}
                </div>
            )}

            {tab === "dosya" && (
                <div className="space-y-4">
                    <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4 text-xs text-slate-300">
                        <strong className="text-cyan-200">Kontör tasarrufu:</strong> Ekspertiz veya fatura
                        evrakından parçalar OCR ile çıkarılır; Bilus araması kontör harcamaz. Fiyat geçmişi
                        yalnızca istediğiniz parça için <strong className="text-white">1 kontör</strong>{" "}
                        harcar (aynı parça aynı gün tekrar ücretsiz).
                    </div>

                    <div className="flex flex-wrap gap-2 items-end rounded-xl border border-white/10 bg-[#121A2F]/50 p-4">
                        <div>
                            <label className="text-[10px] text-slate-500 uppercase">Sigorta dosya no</label>
                            <input
                                value={dosyaNo}
                                onChange={(e) => {
                                    setDosyaNo(e.target.value);
                                    setEvrakList(null);
                                    setKarsilastirma(null);
                                }}
                                placeholder="MBS-03-2025-01"
                                className="mt-1 block rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white w-52 font-mono"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={loadEvrak}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-sm disabled:opacity-50"
                        >
                            <FileText className="h-4 w-4 inline mr-1" />
                            Evrakları listele
                        </button>
                        <button
                            type="button"
                            onClick={() => runEvrakTara(false)}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    <Sparkles className="h-4 w-4 inline mr-1" />
                                    Evraktan tara (0 kontör)
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => runEvrakTara(true)}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg border border-orange-500/40 text-orange-200 text-sm disabled:opacity-50"
                            title="Her parça için fiyat geçmişi — kontör harcar"
                        >
                            Evrak + tüm fiyatlar
                        </button>
                        <button
                            type="button"
                            onClick={() => runKarsilastir(false)}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg border border-violet-500/30 text-violet-300 text-sm disabled:opacity-50"
                        >
                            DK listesi (kontörsüz)
                        </button>
                    </div>

                    {evrakList?.evraklar?.length > 0 ? (
                        <div className="rounded-xl border border-white/10 overflow-hidden">
                            <div className="px-4 py-2 bg-[#121A2F] text-[10px] uppercase text-slate-500">
                                Evrak seçimi — {evrakList.dosya_no || dosyaNo} · {evrakList.plaka} ·{" "}
                                {evrakList.marka}
                            </div>
                            <div className="divide-y divide-white/5 max-h-48 overflow-y-auto">
                                {evrakList.evraklar.map((ev) => (
                                    <label
                                        key={ev.id}
                                        className={`flex items-center gap-3 px-4 py-2 text-xs cursor-pointer ${
                                            ev.bilus_tarama_uygun ? "text-slate-300" : "text-slate-600"
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            disabled={!ev.bilus_tarama_uygun}
                                            checked={seciliEvrak.includes(ev.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSeciliEvrak((s) => [...s, ev.id]);
                                                } else {
                                                    setSeciliEvrak((s) => s.filter((id) => id !== ev.id));
                                                }
                                            }}
                                            className="rounded"
                                        />
                                        <span className="font-mono text-orange-300/80">#{ev.id}</span>
                                        <span className="truncate flex-1">{ev.dosya_adi || "—"}</span>
                                        <span className="text-[10px] uppercase text-slate-500">{ev.kaynak_tipi}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {karsilastirma?.evrak_ozet?.length > 0 ? (
                        <div className="flex flex-wrap gap-2 text-[11px] text-slate-400">
                            {karsilastirma.evrak_ozet.map((e) => (
                                <span
                                    key={e.evrak_id}
                                    className="px-2 py-1 rounded border border-white/10 bg-[#121A2F]/60"
                                >
                                    {e.kaynak_tipi}: {e.parca_sayisi} parça
                                </span>
                            ))}
                        </div>
                    ) : null}

                    {karsilastirma?.parcalar_sonuc ? (
                        <div className="space-y-3">
                            <div className="text-sm text-slate-400">{karsilastirma.ozet?.mesaj}</div>
                            <div className="overflow-x-auto rounded-xl border border-white/10">
                                <table className="w-full text-xs">
                                    <thead className="bg-[#121A2F] text-slate-500 uppercase">
                                        <tr>
                                            <th className="p-2 text-left">Parça</th>
                                            <th className="p-2 text-left">Kaynak</th>
                                            <th className="p-2 text-left">Rapor</th>
                                            <th className="p-2 text-left">Bilus ref.</th>
                                            <th className="p-2 text-left">Durum</th>
                                            <th className="p-2 text-left">Fiyat</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {karsilastirma.parcalar_sonuc.map((p, i) => (
                                            <tr key={i} className="border-t border-white/5">
                                                <td className="p-2 text-white">{p.parca_adi}</td>
                                                <td className="p-2 text-slate-500">{p.kaynak || "—"}</td>
                                                <td className="p-2 text-slate-300">{p.rapor_tutar_fmt || "—"}</td>
                                                <td className="p-2 text-orange-300">{p.referans_fmt || "—"}</td>
                                                <td className="p-2">
                                                    <DurumBadge durum={p.durum} label={p.durum_label} />
                                                </td>
                                                <td className="p-2">
                                                    {p.guncel_fiyat ? (
                                                        <span className="text-emerald-300">
                                                            {p.guncel_fiyat.kdvli ?? p.guncel_fiyat.kdvsiz}
                                                        </span>
                                                    ) : p.secili?.oem_id ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => satirFiyat(p.secili.oem_id, i)}
                                                            className="text-[10px] px-2 py-0.5 rounded bg-orange-600/60 text-white"
                                                        >
                                                            1 kontör
                                                        </button>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                </div>
            )}

            {tab === "ayar" && (
                <div className="max-w-lg space-y-4 rounded-xl border border-white/10 bg-[#121A2F]/50 p-5">
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                        <ShieldCheck className="h-4 w-4 text-orange-400" />
                        Bilus üye giriş bilgileri sunucuda güvenli saklanır; kaynak koda yazılmaz.
                    </div>
                    <div>
                        <label className="text-[10px] text-slate-500 uppercase">E-posta</label>
                        <input
                            type="email"
                            value={ayar.eposta}
                            onChange={(e) => setAyar((a) => ({ ...a, eposta: e.target.value }))}
                            className="w-full mt-1 rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] text-slate-500 uppercase">Şifre (değiştirmek için)</label>
                        <input
                            type="password"
                            value={ayarSifre}
                            onChange={(e) => setAyarSifre(e.target.value)}
                            placeholder={ozet?.sifre_tanimli ? "••••••••" : "Yeni şifre"}
                            className="w-full mt-1 rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                        <input
                            type="checkbox"
                            checked={ayar.aktif}
                            onChange={(e) => setAyar((a) => ({ ...a, aktif: e.target.checked }))}
                            className="rounded"
                        />
                        Entegrasyon aktif
                    </label>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={kaydetAyar}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium"
                        >
                            Kaydet
                        </button>
                        <button
                            type="button"
                            onClick={testGiris}
                            disabled={loading}
                            className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300"
                        >
                            Giriş testi
                        </button>
                    </div>
                    {ozet?.son_giris ? (
                        <div className="text-[11px] text-emerald-400 flex items-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Son başarılı giriş kayıtlı
                        </div>
                    ) : null}
                </div>
            )}

            {loading && tab !== "ayar" && (
                <div className="flex justify-center py-8">
                    <Loader2 className="h-7 w-7 animate-spin text-orange-400" />
                </div>
            )}
        </div>
    );
}
